import { slopeAt, type Heightfield } from './heightfield.js';
import { between, deriveSeed, mulberry32 } from './rand.js';
import { surfaceAt, type SurfaceId, type SurfaceSegment } from './surfaces.js';

/**
 * Where the things she is fetching lie along the route.
 *
 * Pure positions in metres. `src/drive/drive-screen.ts` turns them into sprites resting on the ground
 * and into the contact test that collects them, the same division of labour `src/core/world/props.ts`
 * has with `src/drive/props.ts`.
 *
 * The one rule that matters here: **an item cannot be missed.** The route is one-dimensional and
 * collection is on contact, so she passes over every metre of it - unless she is in the air over that
 * metre, or the thing is under a log, or it is sitting on a slope she rolls away from. Getting this
 * wrong means driving up and down hunting for one item, which is the exact frustration the loop exists
 * to avoid, and it is unrecoverable: the way home does not open until the trailer is full.
 *
 * So placement is deliberately *not* the rejection sampling the patches and props use. Those are allowed
 * to give up and place fewer - a route with one puddle short is still a route. Here, fewer items than the
 * loop state expects would leave her stranded, so items go in fixed slots and slide along the road until
 * they find ground that satisfies every rule at once. `placeItems` returns what it actually placed and
 * nothing derives a count independently of that array.
 *
 * That is also why the load goes down *before* the props: the load is the only thing on a route whose
 * absence breaks the drive rather than thinning it, so it gets first refusal on the middle of the route
 * and the logs and ramps fit around what is left. It was the other way round for one commit, and a
 * four-item meadow route came back with three because the props had taken every flat spot.
 */

export interface ItemSpec {
  /** Where it rests, in metres from the Lookout. She drives over it; there is no y to choose. */
  readonly atM: number;
}

/** Two things to fetch is the fewest that teaches "more than one". */
export const ITEM_COUNT_MIN = 2;
/**
 * Four is the most.
 *
 * The ceiling is her patience, not the physics: at the speed she actually drives, four items already
 * means a 104-metre route and about forty seconds of driving. A fifth costs another twenty-six metres
 * and buys no new idea.
 */
export const ITEM_COUNT_MAX = 4;
/**
 * Least route per item.
 *
 * Sized from the pace rather than the distance: at the ~3.4 m/s she manages with the hills and the
 * throttle ramp, twenty-six metres is about seven and a half seconds between one pickup and the next.
 * Two items eight seconds apart are two moments; two items three seconds apart are one.
 */
export const ITEM_SPAN_MIN_M = 26;
/** Least distance between two items, and between an item and either end of the route. */
export const ITEM_GAP_MIN_M = 18;
/**
 * How far either side of an item no prop may stand - one car length.
 *
 * A ramp is the case this exists for. She launches off it and is in the air for the next couple of
 * metres, and an item under that arc is an item she drives *over* without touching. Enforced from the
 * prop's side, in `placeProps`, because the items are laid first.
 */
export const ITEM_CLEAR_OF_PROPS_M = 4.5;
/**
 * Flat enough to stop on: a fifth of the terrain clamp.
 *
 * This is a query against the generated heightfield, not the `flatMarginM` promise - that one only
 * covers the two ends of the route, and every item but the last is in the middle of it.
 */
export const ITEM_MAX_GROUND_SLOPE = 0.06;
/** How far either side of an item the flatness has to hold. About half a wheelbase each way. */
export const ITEM_FLAT_SPAN_M = 1;
/** How far an item may slide from its nominal slot looking for ground that suits it. */
export const ITEM_SLIDE_MAX_M = 8;

/**
 * Ground an item would rather not rest on.
 *
 * Not about grip, and not about missing it: it is about whether she can see the thing. An item in a
 * puddle is half underwater, an item in a mud wallow is the same brown as the mud, and an item on ice
 * is one she skates past before she has registered it.
 */
const UNSUITABLE: readonly SurfaceId[] = ['water', 'mud', 'ice'];
/**
 * Ground an item may never rest on, whatever it costs.
 *
 * Only water. Everything else on the list above is a thing she can still see and still drive over; a
 * submerged item is one she has no way of knowing is there.
 */
const FORBIDDEN: readonly SurfaceId[] = ['water'];

/**
 * How many things to fetch, from the difficulty drift.
 *
 * Two at first, three or four later. Same smoothstepped `difficulty` that lengthens the route and
 * roughens the ground, so the load grows with everything else rather than as its own surprise.
 */
export function itemCountFor(seed: number, difficulty: number): number {
  const d = Number.isFinite(difficulty) ? Math.max(0, Math.min(1, difficulty)) : 0;
  const rand = mulberry32(deriveSeed(seed, 'items'));
  const spread = ITEM_COUNT_MAX - ITEM_COUNT_MIN;
  const count = ITEM_COUNT_MIN + Math.round(d * spread * (0.7 + 0.6 * rand()));
  return Math.max(ITEM_COUNT_MIN, Math.min(ITEM_COUNT_MAX, count));
}

export interface PlaceItemsOptions {
  readonly seed: number;
  readonly count: number;
  readonly lengthM: number;
  readonly field: Heightfield;
  /** The patches, which are laid before the load. Props come after it and keep clear of it. */
  readonly surfaces: readonly SurfaceSegment[];
}

/**
 * Lay the load out along the route.
 *
 * The last one always sits at `lengthM`, where the single goal item has always sat: that spot is
 * already guaranteed level and clear of everything by the route generator's own end margins, and it is
 * what makes "the last one opens the way home" fall out of the arrival test the drive screen already
 * has rather than needing a rule of its own.
 */
export function placeItems(options: PlaceItemsOptions): readonly ItemSpec[] {
  const { lengthM, field, surfaces } = options;
  const count = Math.max(1, Math.min(ITEM_COUNT_MAX, Math.round(options.count)));
  const rand = mulberry32(deriveSeed(options.seed, 'item-slots'));

  /*
   * Three attempts per slot, each giving up something the one before it insisted on.
   *
   * The order is a ranking of what actually matters, and the last tier is the important one. A pretty
   * spot is worth having; a *full load* is worth more than any of it, because one item short is a
   * trailer that never fills. Hill country at full difficulty is what forced this: a 12-metre hill
   * wavelength with half a metre of roughness leaves barely a flat metre anywhere, and the mud and the
   * stream take what is left. Two tiers came back with three items out of four.
   *
   * Note what the last tier keeps: the gaps, so the pacing survives, and water, so the thing is never
   * invisible. It drops the flatness bound entirely, and that is safe rather than a compromise -
   * flatness was never what made an item collectable. She drives over every metre of the route; the
   * only way to pass over one without touching it is to be in the air, and that is the props' clearance
   * rule, enforced in `placeProps` because the load is laid first. An item sits on the ground at
   * whatever height the ground is, so a slope costs a little tidiness and nothing else.
   */
  const TIERS: readonly { readonly maxSlope: number; readonly reach: number; readonly avoid: readonly SurfaceId[] }[] =
    [
      { maxSlope: ITEM_MAX_GROUND_SLOPE, reach: ITEM_SLIDE_MAX_M, avoid: UNSUITABLE },
      { maxSlope: ITEM_MAX_GROUND_SLOPE * 2, reach: lengthM / (2 * count), avoid: UNSUITABLE },
      { maxSlope: Number.POSITIVE_INFINITY, reach: lengthM, avoid: FORBIDDEN },
    ];

  const placed: number[] = [];
  const suits = (x: number, maxSlope: number, avoid: readonly SurfaceId[]): boolean => {
    if (x < ITEM_GAP_MIN_M || x > lengthM - ITEM_GAP_MIN_M) return false;
    const previous = placed.at(-1);
    if (previous !== undefined && x - previous < ITEM_GAP_MIN_M) return false;
    if (avoid.includes(surfaceAt(surfaces, x, 'grass'))) return false;
    if (!Number.isFinite(maxSlope)) return true;
    // Flat across the whole span, not just under the middle of it: an item measured on the very top of
    // a crest reads as balanced there rather than resting.
    for (let t = x - ITEM_FLAT_SPAN_M; t <= x + ITEM_FLAT_SPAN_M; t += field.spacingM) {
      if (Math.abs(slopeAt(field, t)) > maxSlope) return false;
    }
    return true;
  };

  /** Walk outwards from `from` in ground samples, taking the first spot that satisfies every rule. */
  const slide = (from: number, tier: (typeof TIERS)[number]): number | null => {
    if (suits(from, tier.maxSlope, tier.avoid)) return from;
    for (let offset = field.spacingM; offset <= tier.reach; offset += field.spacingM) {
      if (suits(from - offset, tier.maxSlope, tier.avoid)) return from - offset;
      if (suits(from + offset, tier.maxSlope, tier.avoid)) return from + offset;
    }
    return null;
  };

  for (let i = 1; i < count; i++) {
    const nominal = (lengthM * i) / count + between(rand, -2, 2);
    let x: number | null = null;
    for (const tier of TIERS) {
      x = slide(nominal, tier);
      if (x !== null) break;
    }
    /*
     * Nothing anywhere on the route: drop it and return a shorter list.
     *
     * Unreachable in practice - the last tier will take any ground that is not underwater - but the
     * honest failure has to be a shorter shopping list rather than a count that lies, because the count
     * everyone downstream reads is `items.length`. `tests/items.test.ts` asserts it never happens.
     */
    if (x !== null) placed.push(x);
  }

  placed.push(lengthM);
  return placed.map((atM) => ({ atM }));
}
